import { useEffect, useState, type FormEvent } from "react";
import { useAuth } from "../../app/AuthContext";
import { navigate } from "../../app/routes";
import type { JsonObject, KnowledgeCardDetail, KnowledgeCardDraftRead } from "../../shared/api/types";
import { humanizeKey } from "../../shared/lib/format";
import { Button, Callout, Field, Input, Modal, Select, Textarea } from "../../shared/ui/Primitives";
import { useToast } from "../../shared/ui/Toast";
import {
  editorFilename,
  editorFolder,
  extraProperties,
  parseExtraProperties,
  relationKeys,
  relationText,
  stripLeadingTitle,
  targetFolders,
} from "./cardEditor";

const cardTypes = ["изделие", "правило", "источник", "понятие", "карта", "сценарий", "регламент", "ограничение", "пример"];
const cardStatuses = ["черновик", "действует", "устарела"];

export function CardEditorModal({
  open,
  detail,
  onClose,
  onSubmitted,
}: {
  open: boolean;
  detail: KnowledgeCardDetail | null;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const { api } = useAuth();
  const { showToast } = useToast();
  const [title, setTitle] = useState("");
  const [folder, setFolder] = useState<typeof targetFolders[number]>("10 Карточка");
  const [filename, setFilename] = useState("");
  const [cardType, setCardType] = useState("изделие");
  const [productLevel, setProductLevel] = useState("");
  const [status, setStatus] = useState("черновик");
  const [reviewed, setReviewed] = useState(false);
  const [relations, setRelations] = useState<Record<string, string>>({});
  const [propertiesJson, setPropertiesJson] = useState("{}");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState<KnowledgeCardDraftRead | null>(null);

  useEffect(() => {
    if (!open) return;
    const properties = detail?.properties ?? {};
    setTitle(detail?.title ?? "");
    setFolder(editorFolder(detail));
    setFilename(editorFilename(detail));
    setCardType(typeof properties["тип_карточки"] === "string" ? properties["тип_карточки"] : "изделие");
    setProductLevel(typeof properties["уровень_изделия"] === "string" ? properties["уровень_изделия"] : "");
    setStatus(typeof properties["статус"] === "string" ? properties["статус"] : "черновик");
    setReviewed(properties["проверено"] === true);
    setRelations(Object.fromEntries(relationKeys.map((key) => [key, relationText(properties, key)])));
    setPropertiesJson(JSON.stringify(extraProperties(properties), null, 2));
    setBody(stripLeadingTitle(detail?.body_markdown ?? ""));
    setSaving(false);
    setError("");
    setSaved(null);
  }, [detail, open]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    let properties: JsonObject;
    try {
      properties = parseExtraProperties(propertiesJson);
    } catch (parseError) {
      setError(parseError instanceof Error ? parseError.message : "Некорректный JSON свойств");
      return;
    }
    properties["тип_карточки"] = cardType;
    properties["статус"] = status;
    properties["проверено"] = reviewed;
    if (productLevel) properties["уровень_изделия"] = productLevel;
    for (const key of relationKeys) {
      const values = (relations[key] ?? "").split(/\r?\n/).map((item) => item.trim()).filter(Boolean);
      if (values.length) properties[key] = values;
    }
    setSaving(true);
    try {
      const response = await api.saveKnowledgeCardDraft({
        external_id: detail?.external_id ?? null,
        expected_version_id: detail?.version_id ?? null,
        target_folder: folder,
        filename: filename.trim(),
        title: title.trim(),
        properties,
        body_markdown: body,
      });
      setSaved(response);
      onSubmitted();
      showToast("Карточка поставлена на проверку", "success");
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Не удалось сохранить карточку");
    } finally {
      setSaving(false);
    }
  }

  async function download() {
    if (!saved) return;
    try {
      const result = await api.downloadImportFile(saved.file_id);
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = result.filename || saved.filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (downloadError) {
      showToast(downloadError instanceof Error ? downloadError.message : "Не удалось скачать Markdown", "danger");
    }
  }

  return (
    <Modal
      open={open}
      title={detail ? `Редактирование ${detail.external_id}` : "Новая карточка"}
      onClose={onClose}
      wide
      className="card-editor-modal"
      footer={saved ? (
        <>
          <Button variant="secondary" onClick={onClose}>Закрыть</Button>
          <Button variant="secondary" onClick={() => { onClose(); navigate({ name: "knowledge", tab: "imports" }); }}>Перейти к проверке</Button>
          <Button icon="download" onClick={() => void download()}>Скачать Markdown</Button>
        </>
      ) : (
        <>
          <Button variant="secondary" onClick={onClose}>Отмена</Button>
          <Button form="knowledge-card-editor" type="submit" disabled={saving || !title.trim() || !filename.trim()}>{saving ? "Сохраняем…" : "Сохранить и проверить"}</Button>
        </>
      )}
    >
      {saved ? (
        <div className="card-editor-result">
          <Callout tone="success" title={`${saved.external_id} сохранена как кандидат`}>
            Worker проверит карточку и сформирует новый снимок базы. Активная версия пока не изменена.
          </Callout>
          <dl className="meta-list">
            <div><dt>Путь в Obsidian</dt><dd><code>{saved.source_path}</code></dd></div>
            <div><dt>Задание проверки</dt><dd><code>{saved.job.id}</code></dd></div>
          </dl>
          <Callout tone="warning" title="Не забудьте Obsidian">
            {detail
              ? saved.source_path === detail.source_path
                ? "После успешной проверки скачайте файл и замените им существующую карточку в хранилище."
                : `После успешной проверки удалите ${detail.source_path} и положите скачанный файл по новому пути.`
              : "После успешной проверки скачайте файл и положите его по указанному пути в хранилище."}
          </Callout>
        </div>
      ) : (
        <form id="knowledge-card-editor" className="card-editor-form" onSubmit={submit}>
          <Callout tone="info">
            Сохранение создаст задание проверки и кандидатный снимок. Публикация выполняется отдельно.
          </Callout>
          {error ? <Callout tone="danger">{error}</Callout> : null}
          <div className="form-grid">
            <Field label="Название карточки" className="form-grid__wide"><Input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={500} autoFocus /></Field>
            <Field label="Папка Obsidian" hint={detail ? "Для существующей карточки определяется её ID." : "Определяет реестр нового ID."}>
              <Select value={folder} disabled={Boolean(detail)} onChange={(event) => setFolder(event.target.value as typeof targetFolders[number])}>{targetFolders.map((item) => <option key={item}>{item}</option>)}</Select>
            </Field>
            <Field label="Имя файла"><Input value={filename} onChange={(event) => setFilename(event.target.value)} maxLength={500} /></Field>
            <Field label="Тип карточки"><Select value={cardType} onChange={(event) => setCardType(event.target.value)}>{cardTypes.map((item) => <option key={item}>{item}</option>)}</Select></Field>
            <Field label="Статус"><Select value={status} onChange={(event) => setStatus(event.target.value)}>{cardStatuses.map((item) => <option key={item}>{item}</option>)}</Select></Field>
            <Field label="Уровень изделия"><Select value={productLevel} onChange={(event) => setProductLevel(event.target.value)}><option value="">Не задан</option><option value="семейство">семейство</option><option value="SKU">SKU</option></Select></Field>
            <label className="checkbox-row card-editor-reviewed"><input type="checkbox" checked={reviewed} onChange={(event) => setReviewed(event.target.checked)} />Проверена специалистом</label>
          </div>
          <details className="card-editor-section">
            <summary>Связи карточки</summary>
            <p>Одна Obsidian-ссылка на строку, например <code>[[Название карточки]]</code>.</p>
            <div className="form-grid">
              {relationKeys.map((key) => <Field key={key} label={humanizeKey(key)}><Textarea rows={3} value={relations[key] ?? ""} onChange={(event) => setRelations((current) => ({ ...current, [key]: event.target.value }))} /></Field>)}
            </div>
          </details>
          <Field label="Остальные структурированные свойства (JSON)" hint="Типы значений сохраняются: числа, строки, списки и логические значения.">
            <Textarea className="code-editor card-editor-properties" rows={14} value={propertiesJson} onChange={(event) => setPropertiesJson(event.target.value)} spellCheck={false} />
          </Field>
          <Field label="Содержание Markdown" hint="Заголовок первого уровня будет сформирован из названия карточки.">
            <Textarea className="code-editor card-editor-body" rows={18} value={body} onChange={(event) => setBody(event.target.value)} spellCheck={false} />
          </Field>
        </form>
      )}
    </Modal>
  );
}
